<!-- 
Panel for attempting MFA verification challenge
-->
<template>
  <v-form ref="form">
    <v-card>
      <v-toolbar v-if="dialog" color="secondary" flat dark>
        <v-toolbar-title>Enter verification code</v-toolbar-title>
        <v-spacer/>
        <our-user-guide-button topic="common" section="accessing-the-system"/>
      </v-toolbar>
      <our-busy-indicator :show="state.busy" />
      <template v-if="verificationMethod == 'Email'">
        <v-card-text>
            <v-container fluid class="pa-0">
              <p class="subheading">
                Please enter the verification code sent to your email.
              </p>
              <v-layout wrap>
                <v-flex>
                  <v-text-field
                      :value="authVerificationMessage.verificationCode"
                      :rules="[rules.required]"
                      required
                      autofocus persistent-hint
                      label="Code" hint="The value is case-sensitive"
                      @input="verificationCodeChanged($event)"
                    />
                </v-flex>
              </v-layout>
            </v-container>
            <our-validation-dialog v-model="showValidationDialog" :form="this.$refs.form" :state-error="state.error" show-security-errors />
            <our-validation-dialog v-model="showFormValidationDialog" :form="$refs.form" />
            <our-validation-dialog v-model="showSpecifiedValidationDialog" :specified-error="specifiedValidationError" :fixable="isErrorFixable" @close="onErrorValidationDialogClosed" />
        </v-card-text>
      </template>
      <template v-if="verificationMethod == 'Authenticator'">
        <v-card-text>
              <v-container fluid class="pa-0">
                <p class="subheading">                  
                  Please enter the verification code generated by your authenticator app.
                </p>
                <v-layout wrap>
                  <v-flex>
                    <v-text-field
                        :value="authVerificationMessage.verificationCode"
                        :rules="[rules.required, rules.number, rules.greaterThanZero]"
                        autofocus persistent-hint
                        label="Code" hint="Code generated by your authenticator app."
                        required
                        @input="verificationCodeChanged($event)"
                      />
                  </v-flex>
                </v-layout>
              </v-container>
              <our-validation-dialog v-model="showValidationDialog" :form="this.$refs.form" :state-error="state.error" show-security-errors />
              <our-validation-dialog v-model="showFormValidationDialog" :form="$refs.form" />
              <our-validation-dialog v-model="showSpecifiedValidationDialog" :specified-error="specifiedValidationError" :fixable="isErrorFixable" @close="onErrorValidationDialogClosed" />
          </v-card-text>
      </template>
      <v-card-actions>
        <template v-if="dialog">
          <our-user-guide-button topic="common" section="mfa-verification"/>
          <v-spacer/>
          <v-btn :disabled="state.busy" flat to="/sign-in">Cancel</v-btn>
          <v-btn 
            :disabled="!canSubmit" 
            flat 
            color="primary" 
            type="submit" 
            @click.prevent="submit"
          >Verify & Sign-In</v-btn>
        </template>
        <template v-else>
          <v-flex shrink class="ml-3 mb-3">
            <our-user-guide-button topic="common" section="mfa-verification"/>
          </v-flex>
          <v-flex grow class="text-sm-right mb-3">
            <v-btn :disabled="state.busy" to="/sign-in">Cancel</v-btn>
            <v-btn 
              :disabled="!canSubmit" 
              type="submit" 
              class="mr-2" 
              color="primary" 
              @click.prevent="submit"
            >
              Verify & Sign-In
            </v-btn>
          </v-flex>
        </template>
      </v-card-actions>
    </v-card>
  </v-form>
</template>

<script>
import * as action from "@/store/users/action-types";
import ruleMixins from "@/mixins/rule.mixins";
import moment from "moment";

export default {
  mixins: [ruleMixins],
  props: {
    dialog: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showValidationDialog: false,
      showFormValidationDialog: false,
      showSpecifiedValidationDialog: false,
      specifiedValidationError: null,
      isErrorFixable: true,
      authVerificationMessage: {
        verificationCode: ""
      },
      mfaSignIn: {
        email: "",
        verificationMethod: "",
      }
    };
  },
  computed: {
    state() {
      return this.$store.state.users.user;
    },
    containerCss() {
      return this.dialog ? "grid-list-md pa-0" : "grid-list-xl pa-3";
    },
    email() {
      return this.state.details.email;
    },
    canSubmit() {
      if (this.state.busy) {
        return false;
      }

      if (!this.state.details.requireAuthVerification) {
        return false;
      }

      const tokenExpired = moment.utc(this.state.details.tokenExpiresUtc).isBefore();
      return !tokenExpired;
    },
    verificationMethod() {
      return this.state.details.multiFactorAuthenticationMethod;
    }
  },
  
  methods: {
    validate() {
      if (!this.$refs.form.validate()) {
        this.showFormValidationDialog = true;
        return false;
      }

      return true;
    },
    async submit() {
      if (!this.validate()) {
        return false;
      }

      let success = await this.$store.dispatch(action.SIGN_IN_ATTEMPT_MFA_CHALLENGE, this.authVerificationMessage);
      if (!success) {
        this.showValidationDialog = true;
        return false;
      }

      if (this.state.isSignedIn)
      {
        this.redirectToHome();
        return true;
      }

      this.isErrorFixable = true;
        this.specifiedValidationError = this.state.mfaChallengeAttempt.validationError;

        if (this.state.mfaChallengeAttempt.maxAttemptsExceeded) {
          this.specifiedValidationError = "Incorrect code. Your account has been locked due to too many failed attempts. Please contact helpdesk to get it unlocked.";
          this.isErrorFixable = false;
        }

        if (this.state.mfaChallengeAttempt.failedReason == "CodeExpired") {
          this.specifiedValidationError = "Code expired. Login again to get a new code.";
          this.isErrorFixable = false;
        }

        this.showSpecifiedValidationDialog = true;
        this.authVerificationMessage.verificationCode = null;
        return false;
      
    },
    verificationCodeChanged(newCode) {
      this.authVerificationMessage.verificationCode = newCode;
    },
    onErrorValidationDialogClosed() {
      if (!this.isErrorFixable) {
        this.redirectToHome();
      }
    },
    redirectToHome() {
      this.$router.push("/");
      this.$emit("changed");  
    }
  }
};
</script>
